import { useState, useEffect, useRef } from "react";
import * as Sentry from '@sentry/react';
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "@/App";
import { Send, ArrowLeft, ChevronRight, BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/AuthProvider";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ArticleListItem {
  id: string;
  title: string;
  slug: string;
  category: string;
  order: number;
}

interface ArticleDetail extends ArticleListItem {
  content: string;
}

type View = "home" | "all" | "article";

// ─── Category accent colours ──────────────────────────────────────────────────

const CATEGORY_GRADIENT: Record<string, string> = {
  "Getting Started": "from-blue-500/20 to-blue-600/5",
  "Advanced":        "from-purple-500/20 to-purple-600/5",
  "Billing":         "from-green-500/20 to-green-600/5",
  "Invoices":        "from-amber-500/20 to-amber-600/5",
};

function categoryGradient(cat: string) {
  return CATEGORY_GRADIENT[cat] ?? "from-slate-500/20 to-slate-600/5";
}

// Per-position thumbnail gradients for the three pinned featured cards
const FEATURED_GRADIENTS = [
  "from-[#4A6CF7]/35 via-blue-500/15 to-indigo-400/5",   // brand blue
  "from-[#A259FF]/35 via-purple-500/15 to-violet-400/5", // brand purple
  "from-emerald-500/30 via-teal-500/15 to-cyan-400/5",   // teal/green
];

// Per-position thumbnail images (null = gradient only)
const FEATURED_IMAGES = [
  "https://res.cloudinary.com/dlmbskxuc/image/upload/v1782818633/firsthold_CAL_illustration_mp6qob.png",
  "https://res.cloudinary.com/dlmbskxuc/image/upload/v1782816155/import_data_FH_illustration_mna9k5.png",
  "https://res.cloudinary.com/dlmbskxuc/image/upload/v1782842423/firsthold_data_illustration_kyjsm5.png",
];


// ─── Module-level cache — survives remounts within the same JS session ────────
// Prevents re-fetching Notion on every /help navigation.

let _articlesList: ArticleListItem[] | null = null;
let _contentCache: Record<string, ArticleDetail> = {};

// ─── Component ───────────────────────────────────────────────────────────────

export default function HelpPage() {
  const { isDark } = useTheme();
  const { user } = useAuth();

  const [searchParams] = useSearchParams();
  const autoSlug        = searchParams.get("article");
  const autoOpenedRef   = useRef(false);

  // Scroll to top on every mount (prevents inheriting scroll from the referring page)
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);

  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>([false, false, false]);

  const [feedbackText, setFeedbackText]     = useState("");
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackSent, setFeedbackSent]       = useState(false);
  const [view, setView]                 = useState<View>(autoSlug ? "article" : "home");
  const [articles, setArticles]         = useState<ArticleListItem[]>(_articlesList ?? []);
  const [selectedArticle, setSelectedArticle] = useState<ArticleDetail | null>(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [articlesLoading, setArticlesLoading]   = useState(_articlesList === null);
  const [articleLoading, setArticleLoading]     = useState<boolean>(!!autoSlug && !_contentCache[autoSlug ?? '']);
  const [contentCache, setContentCache]         = useState<Record<string, ArticleDetail>>(_contentCache);

  useEffect(() => {
    if (_articlesList !== null) return; // already cached from a prior mount
    // Fetch list only first (fast — no content) so cards appear immediately
    fetch("/api/help-content")
      .then(r => r.json())
      .then((data: ArticleListItem[]) => {
        if (!Array.isArray(data)) return;
        _articlesList = data;
        setArticles(_articlesList);
      })
      .catch(() => {})
      .finally(() => setArticlesLoading(false));
    // Background prefetch all article content so it's ready when clicked
    fetch("/api/help-content?all=true")
      .then(r => r.json())
      .then((data: ArticleDetail[]) => {
        if (!Array.isArray(data)) return;
        data.forEach(article => { _contentCache[article.slug] = article; });
        setContentCache({ ..._contentCache });
      })
      .catch(() => {});
  }, []);

  const openArticle = async (item: ArticleListItem) => {
    // Serve from cache if already prefetched (check module cache first)
    const cached = _contentCache[item.slug] ?? contentCache[item.slug];
    if (cached) {
      setSelectedArticle(cached);
      setView("article");
      return;
    }
    setArticleLoading(true);
    setSelectedArticle(null);
    setView("article");
    try {
      const r    = await fetch(`/api/help-content?slug=${item.slug}`);
      const data = await r.json();
      if (data.content !== undefined) {
        const detail = data as ArticleDetail;
        setSelectedArticle(detail);
        setContentCache(prev => ({ ...prev, [item.slug]: detail }));
      }
    } catch {}
    finally { setArticleLoading(false); }
  };

  // Auto-open article when arriving via /help?article=<slug>
  // Fetches directly by slug — no need to wait for the full article list.
  useEffect(() => {
    if (!autoSlug || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    const cached = _contentCache[autoSlug] ?? contentCache[autoSlug];
    if (cached) {
      setSelectedArticle(cached);
      setView("article");
      return;
    }
    setView("article");
    setArticleLoading(true);
    fetch(`/api/help-content?slug=${autoSlug}`)
      .then(r => r.json())
      .then(data => {
        if (data.content !== undefined) {
          const detail = data as ArticleDetail;
          setSelectedArticle(detail);
          _contentCache[autoSlug] = detail;
          setContentCache(prev => ({ ...prev, [autoSlug]: detail }));
        }
      })
      .catch(() => {})
      .finally(() => setArticleLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSlug]);

  const goHome = () => { setView("home"); setSelectedArticle(null); setSelectedCategory("all"); };

  const categories = ["all", ...Array.from(new Set(articles.map(a => a.category).filter(Boolean)))];
  const filtered   = selectedCategory === "all" ? articles : articles.filter(a => a.category === selectedCategory);
  const FEATURED_TITLES = [
    "how to add your first booking",
    "how to import your booking history",
    "how to read your stats",
  ];
  const featured = FEATURED_TITLES
    .map(t => articles.find(a => a.title.toLowerCase().includes(t)))
    .filter((a): a is ArticleListItem => !!a);

  // ── Styles ──────────────────────────────────────────────────────────────────
  const pageClasses = isDark
    ? "bg-gradient-to-b from-[#0f1419] via-[#131820] to-[#0a0e12] text-white"
    : "bg-[#E3E8F1] text-[#1A1F36]";

  const cardClasses = cn(
    "backdrop-blur-2xl rounded-[2.5rem] border shadow-xl shadow-blue-900/5",
    isDark ? "bg-white/10 border-white/10" : "bg-white/80 border-white"
  );

  const inputClasses = cn(
    "w-full px-6 py-4 rounded-2xl border transition-all outline-none",
    isDark
      ? "bg-white/5 border-white/10 text-white placeholder-white/20 focus:border-white/30"
      : "bg-white/50 border-gray-200 text-[#1A1F36] placeholder-[#1A1F36]/30 focus:border-gray-400"
  );

  const labelClasses = "text-sm font-bold uppercase tracking-[0.2em] mb-3 block opacity-40";

  const pillBase     = "px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.15em] transition-all cursor-pointer select-none";
  const pillActive   = isDark ? "bg-white text-[#1A1F36]" : "bg-[#1A1F36] text-white";
  const pillInactive = isDark ? "bg-white/10 text-white/60 hover:bg-white/20" : "bg-black/5 text-[#1A1F36]/50 hover:bg-black/10";

  return (
    <div className={`min-h-screen ${pageClasses} font-sans selection:bg-blue-200 relative overflow-hidden`}>
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        </div>
      )}

      <main className="max-w-[1400px] mx-auto relative z-10 p-8 pt-4 pb-28 md:pb-8 space-y-10">

        {/* ── Header ── */}
        <header>
          <h1 className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-2">Help</h1>
          <p className={cn("ml-1.5 font-light text-base sm:text-lg md:text-xl", isDark ? "text-white/50" : "text-[#1A1F36]/50")}>
            Learn how to get the most out of Firsthold.
          </p>
        </header>

        <AnimatePresence mode="wait">

          {/* ════════════════════════════════════════════════════
              HOME VIEW — featured cards + feedback
          ════════════════════════════════════════════════════ */}
          {view === "home" && (
            <motion.div
              key="home"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="space-y-10"
            >
              {/* Featured article cards */}
              <div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
                  {articlesLoading
                    ? [0, 1, 2].map(i => (
                        <div key={i} className={cn("rounded-3xl overflow-hidden border", isDark ? "bg-white/5 border-white/10" : "bg-white/80 border-white")} style={{ animationDelay: `${i * 0.08}s` }}>
                          {/* Thumbnail skeleton */}
                          <div className={cn("h-[100px] animate-pulse", isDark ? "bg-white/8" : "bg-black/6")} style={{ animationDelay: `${i * 0.08}s` }} />
                          {/* Card body skeleton */}
                          <div className="p-5 space-y-2.5">
                            <div className={cn("h-3.5 rounded-full animate-pulse", isDark ? "bg-white/10" : "bg-black/8")} style={{ width: `${[75, 85, 70][i]}%`, animationDelay: `${i * 0.08 + 0.05}s` }} />
                            <div className={cn("h-3 rounded-full animate-pulse", isDark ? "bg-white/6" : "bg-black/5")} style={{ width: `${[55, 60, 50][i]}%`, animationDelay: `${i * 0.08 + 0.1}s` }} />
                          </div>
                        </div>
                      ))
                    : featured.length === 0
                    ? (
                        <p className={cn("col-span-3 text-sm font-medium py-8 text-center", isDark ? "text-white/30" : "text-[#1A1F36]/30")}>
                          Help articles coming soon.
                        </p>
                      )
                    : featured.map((article, i) => (
                        <motion.button
                          key={article.id}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.07 }}
                          onClick={() => openArticle(article)}
                          className={cn(
                            "group text-left rounded-3xl border overflow-hidden transition-all hover:scale-[1.02] hover:shadow-lg",
                            isDark ? "bg-white/5 border-white/10 hover:bg-white/10" : "bg-white/80 border-white hover:bg-white"
                          )}
                        >
                          {/* Thumbnail area */}
                          <div className={cn(
                            "h-[100px] relative flex items-end p-4 overflow-hidden",
                            cn("bg-gradient-to-br", FEATURED_GRADIENTS[i] ?? categoryGradient(article.category))
                          )}>
                            {/* Shimmer pulse while image loads */}
                            {FEATURED_IMAGES[i] && !imagesLoaded[i] && (
                              <div className={cn("absolute inset-0 animate-pulse", isDark ? "bg-white/8" : "bg-black/6")} />
                            )}
                            {FEATURED_IMAGES[i] && (
                              <img
                                src={FEATURED_IMAGES[i]!}
                                alt=""
                                className={cn(
                                  "absolute inset-0 w-full h-full object-cover transition-opacity duration-500",
                                  imagesLoaded[i] ? "opacity-100" : "opacity-0"
                                )}
                                onLoad={() => setImagesLoaded(prev => {
                                  const next = [...prev];
                                  next[i] = true;
                                  return next;
                                })}
                                draggable={false}
                              />
                            )}
                            {article.category && (
                              <span className="relative z-10 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] bg-black/55 text-white backdrop-blur-sm">
                                {article.category}
                              </span>
                            )}
                          </div>
                          {/* Card body */}
                          <div className="p-5 flex items-start justify-between gap-3">
                            <h3 className="font-bold text-sm leading-snug line-clamp-2">{article.title}</h3>
                            <ChevronRight size={16} className="shrink-0 mt-0.5 opacity-30 group-hover:opacity-60 transition-opacity" />
                          </div>
                        </motion.button>
                      ))
                  }
                </div>

                {/* Browse all button */}
                {!articlesLoading && articles.length > 0 && (
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    onClick={() => setView("all")}
                    className={cn(
                      "flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold uppercase tracking-[0.15em] border transition-all hover:opacity-80",
                      isDark ? "border-white/20 text-white/60 hover:bg-white/10" : "border-[#1A1F36]/20 text-[#1A1F36]/60 hover:bg-black/5"
                    )}
                  >
                    <BookOpen size={14} />
                    Browse All Help Topics
                    <ChevronRight size={14} />
                  </motion.button>
                )}
              </div>

              {/* Share Feedback */}
              <div className={cn(cardClasses, "p-8 md:p-12")}>
                <div className="flex items-center gap-3 mb-8">
                  <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", isDark ? "bg-white/5 text-white" : "bg-black text-white")}>
                    <Send size={20} />
                  </div>
                  <h2 className="text-2xl font-black">Share Feedback</h2>
                </div>
                {feedbackSent ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
                    <div className={cn("w-12 h-12 rounded-full flex items-center justify-center", isDark ? "bg-white/10" : "bg-black/5")}>
                      <Send size={20} className={isDark ? "text-white/60" : "text-black/40"} />
                    </div>
                    <p className={cn("font-bold text-lg", isDark ? "text-white" : "text-[#1A1F36]")}>Thanks for the feedback.</p>
                    <p className={cn("text-sm", isDark ? "text-white/40" : "text-[#1A1F36]/40")}>We read every message.</p>
                    <button
                      onClick={() => { setFeedbackSent(false); setFeedbackText(""); }}
                      className={cn("mt-2 text-xs uppercase tracking-[0.2em] font-bold underline underline-offset-4 opacity-40 hover:opacity-70 transition-opacity", isDark ? "text-white" : "text-[#1A1F36]")}
                    >
                      Send Another
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className={labelClasses}>What could be better?</label>
                      <textarea
                        className={cn(inputClasses, "min-h-[160px] resize-none")}
                        placeholder="Your thoughts..."
                        value={feedbackText}
                        onChange={e => setFeedbackText(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col justify-end">
                      <button
                        onClick={async () => {
                          if (!feedbackText.trim() || feedbackSending) return;
                          setFeedbackSending(true);
                          try {
                            const r = await fetch('/api/loops?action=feedback', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                fromEmail: user?.email,
                                message: feedbackText.trim(),
                              }),
                            });
                            if (!r.ok) {
                              Sentry.captureException(new Error(`Feedback submission failed: ${r.status}`), {
                                tags: { flow: 'feedback' },
                                extra: { fromEmail: user?.email },
                              });
                            }
                          } catch (err) {
                            Sentry.captureException(err, { tags: { flow: 'feedback' } });
                          }
                          setFeedbackSent(true);
                          setFeedbackSending(false);
                        }}
                        disabled={!feedbackText.trim() || feedbackSending}
                        className="w-full bg-black text-white py-5 rounded-full font-bold uppercase tracking-[0.2em] shadow-lg shadow-black/20 hover:opacity-80 transition-opacity active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {feedbackSending ? "Sending…" : "Send Feedback"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* ════════════════════════════════════════════════════
              ALL ARTICLES VIEW
          ════════════════════════════════════════════════════ */}
          {view === "all" && (
            <motion.div
              key="all"
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.2 }}
              className={cn(cardClasses, "p-8 md:p-10")}
            >
              <button
                onClick={goHome}
                className={cn(
                  "flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] mb-8 transition-opacity hover:opacity-70",
                  isDark ? "text-white/50" : "text-[#1A1F36]/50"
                )}
              >
                <ArrowLeft size={14} />
                Back
              </button>

              <h2 className="text-2xl font-black mb-8">All Articles</h2>

              {/* Category pills */}
              {categories.length > 1 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={cn(pillBase, selectedCategory === cat ? pillActive : pillInactive)}
                    >
                      {cat === "all" ? "All" : cat}
                    </button>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                {filtered.map((article, i) => (
                  <motion.button
                    key={article.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    onClick={() => openArticle(article)}
                    className={cn(
                      "w-full text-left flex items-start sm:items-center justify-between gap-4 px-5 py-4 rounded-2xl border transition-all group",
                      isDark
                        ? "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                        : "bg-white/60 border-gray-200 hover:bg-white hover:border-gray-300"
                    )}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 min-w-0">
                      {article.category && (
                        <span className={cn(
                          "shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] self-start",
                          isDark ? "bg-white/10 text-white/50" : "bg-black/5 text-[#1A1F36]/40"
                        )}>
                          {article.category}
                        </span>
                      )}
                      <span className="font-semibold text-sm sm:truncate">{article.title}</span>
                    </div>
                    <ChevronRight size={16} className="shrink-0 mt-0.5 sm:mt-0 opacity-30 group-hover:opacity-60 transition-opacity" />
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* ════════════════════════════════════════════════════
              ARTICLE DETAIL VIEW
          ════════════════════════════════════════════════════ */}
          {view === "article" && (
            <motion.div
              key="article"
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.2 }}
              className={cn(cardClasses, "p-8 md:p-12")}
            >
              <button
                onClick={goHome}
                className={cn(
                  "flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] mb-8 transition-opacity hover:opacity-70",
                  isDark ? "text-white/50" : "text-[#1A1F36]/50"
                )}
              >
                <ArrowLeft size={14} />
                Back to Help
              </button>

              {articleLoading ? (
                <div className="animate-pulse space-y-6 pt-2">
                  {/* Category pill */}
                  <div className={cn("h-5 w-20 rounded-full", isDark ? "bg-white/10" : "bg-black/8")} />
                  {/* Title */}
                  <div className="space-y-3">
                    <div className={cn("h-8 rounded-xl", isDark ? "bg-white/10" : "bg-black/8")} style={{ width: "72%" }} />
                    <div className={cn("h-8 rounded-xl", isDark ? "bg-white/7" : "bg-black/5")} style={{ width: "50%" }} />
                  </div>
                  {/* Body paragraphs */}
                  <div className="space-y-3 pt-4">
                    {[100, 92, 96, 78, 88, 100, 65, 90, 84, 55].map((w, i) => (
                      <div key={i} className={cn("h-3.5 rounded-full", isDark ? "bg-white/7" : "bg-black/6")} style={{ width: `${w}%` }} />
                    ))}
                  </div>
                  {/* Second paragraph block */}
                  <div className="space-y-3 pt-2">
                    {[100, 88, 75, 60].map((w, i) => (
                      <div key={i} className={cn("h-3.5 rounded-full", isDark ? "bg-white/5" : "bg-black/4")} style={{ width: `${w}%` }} />
                    ))}
                  </div>
                </div>
              ) : selectedArticle ? (
                <>
                  {selectedArticle.category && (
                    <span className={cn(
                      "inline-block mb-4 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em]",
                      isDark ? "bg-white/10 text-white/60" : "bg-black/5 text-[#1A1F36]/50"
                    )}>
                      {selectedArticle.category}
                    </span>
                  )}

                  <h2 className="text-2xl md:text-3xl font-black mb-8">{selectedArticle.title}</h2>

                  <div className={cn(
                    "prose max-w-none",
                    isDark ? "prose-invert" : "",
                    "[&_h1]:text-2xl [&_h1]:font-black [&_h1]:mb-4 [&_h1]:mt-8",
                    "[&_h2]:text-xl [&_h2]:font-black [&_h2]:mb-3 [&_h2]:mt-6",
                    "[&_h3]:text-lg [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-5",
                    "[&_p]:mb-4 [&_p]:leading-relaxed [&_p]:opacity-80",
                    "[&_ul]:mb-4 [&_ul]:space-y-1 [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:opacity-80",
                    "[&_ol]:mb-4 [&_ol]:space-y-1 [&_ol]:pl-5 [&_ol]:list-decimal [&_ol]:opacity-80",
                    "[&_li]:leading-relaxed",
                    "[&_strong]:font-bold [&_strong]:opacity-100",
                    "[&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono",
                    isDark ? "[&_code]:bg-white/10" : "[&_code]:bg-black/5",
                    "[&_table]:w-full [&_table]:text-sm [&_table]:mb-6 [&_table]:border-collapse",
                    "[&_th]:text-left [&_th]:font-bold [&_th]:py-2 [&_th]:px-3 [&_th]:opacity-60 [&_th]:uppercase [&_th]:tracking-wider [&_th]:text-[10px]",
                    "[&_td]:py-2 [&_td]:px-3 [&_td]:opacity-80 [&_td]:leading-relaxed",
                    isDark ? "[&_th]:border-b [&_th]:border-white/10 [&_td]:border-b [&_td]:border-white/5" : "[&_th]:border-b [&_th]:border-black/10 [&_td]:border-b [&_td]:border-black/5"
                  )}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{selectedArticle.content}</ReactMarkdown>
                  </div>
                </>
              ) : (
                <p className="opacity-40 text-sm">Article not found.</p>
              )}
            </motion.div>
          )}

        </AnimatePresence>
      </main>
    </div>
  );
}
